import React, { useState, useEffect } from 'react';
import { flightsStat, legs, passengers } from '../../services/api';
import { formatDateTime, formatFlightTimeRange } from '../../utils/dateFormat';
import { flightStatuses, cabinTypes, seatTypes, legsScheduleStatuses } from '../../constants/travelEnums';

const getDisplayFlightStatus = (flight) => {
  // A ticket segment stays confirmed after its associated schedule entry is cancelled.
  // Prefer the current schedule status so My Flights reflects the actual operation.
  if (flight.fstat_tsegment_id != null && flight.lsch_status === 20) {
    return { label: legsScheduleStatuses[20], className: 'badge-danger' };
  }

  return {
    label: flightStatuses[flight.fstat_status],
    className: flight.fstat_status === 10 ? 'badge-info' : 'badge-success'
  };
};

function MyFlights() {
  const [flights, setFlights] = useState([]);
  const [allLegs, setAllLegs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('future'); // 'future', 'past'
  const [passengersList, setPassengersList] = useState([]);
  const [selectedPassengerId, setSelectedPassengerId] = useState(null);
  const [passengersError, setPassengersError] = useState(null);

  const formatDelay = (minutes) => {
    if (minutes === null || minutes === undefined || Number.isNaN(minutes)) return null;
    if (minutes === 0) return { text: 'on time', color: '#7f8c8d' };
    const sign = minutes > 0 ? '+' : '−';
    const abs = Math.abs(minutes);
    return {
      text: `${sign}${abs}m`,
      color: minutes > 0 ? '#c0392b' : '#27ae60'
    };
  };

  useEffect(() => {
    loadPassengers();
    loadLegs();
  }, []);

  useEffect(() => {
    if (selectedPassengerId) {
      loadFlights(selectedPassengerId);
    }
  }, [selectedPassengerId]);

  const loadPassengers = async () => {
    try {
      const response = await passengers.getAll();
      const list = response.data || [];
      setPassengersList(list);
      const preferred = list.find((p) => p.passenger_is_def) || list[0];
      setSelectedPassengerId(preferred ? preferred.passenger_id : null);
      setPassengersError(null);
      if (!preferred) {
        setLoading(false);
      }
    } catch (err) {
      setPassengersError('Failed to load passengers');
      console.error(err);
      setLoading(false);
    }
  };

  const loadFlights = async (passengerId) => {
    try {
      setLoading(true);
      const response = await flightsStat.getAll({ passenger_id: passengerId });
      // Handle both old array format and new object with listData
      const data = Array.isArray(response.data) ? response.data : (response.data.listData || []);
      setFlights(data);
      setError(null);
    } catch (err) {
      setError('Failed to load flights');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadLegs = async () => {
    try {
      const response = await legs.getAll();
      setAllLegs(response.data);
    } catch (err) {
      console.error('Failed to load legs', err);
    }
  };

  // Get 'Operated as' text for a leg id (returns string or null)
  const getOperatedByForLegId = (legId) => {
    if (!legId) return null;
    const leg = allLegs.find(l => l.leg_id === legId);
    if (!leg || !leg.leg_operational_leg_id) return null;
    const operationalLeg = allLegs.find(l => l.leg_id === leg.leg_operational_leg_id);
    if (!operationalLeg) return null;
    return `${operationalLeg.carrier_iata || operationalLeg.carrier_name} ${operationalLeg.flight_number}`;
  };

  // Sort helper: compare by departure date and time
  const sortByDeparture = (a, b, ascending = true) => {
    const dateA = `${a.lsch_dep_date} ${a.lsch_dep_time || '00:00'}`;
    const dateB = `${b.lsch_dep_date} ${b.lsch_dep_time || '00:00'}`;
    return ascending ? dateA.localeCompare(dateB) : dateB.localeCompare(dateA);
  };

  // Only show flights with confirmed segments (status = 10)
  // Upcoming: confirmed segments WITHOUT usage result - sorted ascending (nearest first)
  // Past: confirmed segments WITH usage result = 10 (Used) - sorted descending (most recent first)
  const upcomingFlights = flights
    .filter(f => f.tsegment_status === 10 && !f.tsegment_usage_result)
    .sort((a, b) => sortByDeparture(a, b, true));
  const pastFlights = flights
    .filter(f => f.tsegment_status === 10 && f.tsegment_usage_result === 10)
    .sort((a, b) => sortByDeparture(a, b, false));

  const getFilteredFlights = () => {
    if (filter === 'future') {
      return upcomingFlights;
    } else {
      return pastFlights;
    }
  };

  const filteredFlights = getFilteredFlights();
  const futureFlightsCount = upcomingFlights.length;

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <h2>My Flights</h2>
        </div>
        <p>Loading flights...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="page-header">
          <h2>My Flights</h2>
        </div>
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }

  if (!selectedPassengerId) {
    return (
      <div>
        <div className="page-header">
          <h2>My Flights</h2>
        </div>
        {passengersError && <div className="alert alert-danger">{passengersError}</div>}
        <div className="alert alert-warning">No passengers found. Please create a passenger first.</div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h2>My Flights</h2>
      </div>

      {passengersError && (
        <div className="alert alert-danger">{passengersError}</div>
      )}

      <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            className={`btn ${filter === 'future' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter('future')}
          >
            Upcoming Flights ({futureFlightsCount})
          </button>
          <button
            className={`btn ${filter === 'past' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter('past')}
          >
            Past Flights ({pastFlights.length})
          </button>
        </div>
        <label style={{ color: '#2c3e50', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          Passenger:
          <select
            value={selectedPassengerId}
            onChange={(e) => setSelectedPassengerId(parseInt(e.target.value, 10))}
          >
            {passengersList.map((p) => (
              <option key={p.passenger_id} value={p.passenger_id}>
                {p.passenger_name || `Passenger ${p.passenger_id}`}
                {p.passenger_is_def ? ' (you)' : ''}
              </option>
            ))}
          </select>
        </label>
      </div>

      {filteredFlights.length === 0 ? (
        <div style={{ background: 'white', padding: '40px', borderRadius: '8px', textAlign: 'center', color: '#7f8c8d' }}>
          <p>No flights found for this filter.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '15px' }}>
          {filteredFlights.map((flight) => {
            const displayStatus = getDisplayFlightStatus(flight);
            return (
              <div key={flight.fstat_id} className="flight-card" style={{
                background: 'white',
                padding: '20px',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px', marginBottom: '10px' }}>
                  <div style={{ textAlign: 'center', minWidth: '60px' }}>
                    <div style={{ fontSize: '1.8em', fontWeight: 'bold', color: '#3498db' }}>
                      {flight.lsch_dep_date && (() => {
                        const dateStr = flight.lsch_dep_date.split('T')[0];
                        const [year, month, day] = dateStr.split('-');
                        return day;
                      })()}
                    </div>
                    <div style={{ color: '#7f8c8d', fontSize: '0.9em' }}>
                      {flight.lsch_dep_date && (() => {
                        const dateStr = flight.lsch_dep_date.split('T')[0];
                        const [year, month, day] = dateStr.split('-');
                        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                        return `${monthNames[parseInt(month) - 1]} ${year}`;
                      })()}
                    </div>
                    {filter === 'future' && flight.lsch_dep_date && (
                      <div style={{ color: '#3498db', fontSize: '0.8em', marginTop: '4px' }}>
                        {(() => {
                          const dateStr = flight.lsch_dep_date.split('T')[0];
                          const depDate = new Date(dateStr);
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          depDate.setHours(0, 0, 0, 0);
                          const diffDays = Math.ceil((depDate - today) / (1000 * 60 * 60 * 24));
                          if (diffDays === 0) return 'Today';
                          if (diffDays === 1) return 'Tomorrow';
                          if (diffDays < 0) return '';
                          return `in ${diffDays} days`;
                        })()}
                      </div>
                    )}
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                      <h3 style={{ margin: 0, fontSize: '1.2em', color: '#2c3e50', display: 'flex', alignItems: 'center' }}>
                        {flight.carrier_iata || flight.carrier_name} {flight.flight_number}
                        {getOperatedByForLegId(flight.leg_id) && (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginLeft: '8px',
                              padding: '2px 6px',
                              fontSize: '0.55em',
                              fontWeight: 'normal',
                              color: '#856404',
                              backgroundColor: 'rgba(255, 243, 205, 0.8)',
                              borderRadius: '3px',
                              cursor: 'help'
                            }}
                            title={`Operated as ${getOperatedByForLegId(flight.leg_id)}`}
                          >
                            M
                          </span>
                        )}
                      </h3>
                      <span className={`badge ${displayStatus.className}`}>
                        {displayStatus.label}
                      </span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                      <div style={{ fontSize: '1.4em', fontWeight: 'bold', color: '#2c3e50' }}>{flight.dep_iata}</div>
                      <div style={{ color: '#7f8c8d', fontSize: '1.2em' }}>→</div>
                      <div style={{ fontSize: '1.4em', fontWeight: 'bold', color: '#2c3e50' }}>{flight.arr_iata}</div>
                    </div>

                    <div style={{ color: '#7f8c8d', fontSize: '0.8em' }}>
                      {filter === 'future' ? (
                        <div>{flight.lsch_dep_time} → {flight.lsch_arr_time}</div>
                      ) : (
                        <div style={{ fontSize: '1.05em' }}>
                          {(() => {
                            const hasDepDelay = flight.lflight_dep_utc_min !== null && flight.lsch_dep_utc_min !== null;
                            if (hasDepDelay) {
                              const schedDepLocal = flight.lsch_dep_utc_min + (flight.lsch_dep_offset || 0);
                              const actualDepLocal = flight.lflight_dep_utc_min + (flight.lflight_dep_offset || 0);
                              const depDelay = actualDepLocal - schedDepLocal;
                              if (depDelay === 0) {
                                return (
                                  <>
                                    <span style={{ color: '#2c3e50' }}>{flight.lsch_dep_time}</span>
                                    <span style={{ fontSize: '0.9em', color: '#2ecc71', marginLeft: '8px', fontWeight: 'bold' }}>(on time)</span>
                                  </>
                                );
                              }
                              if (schedDepLocal !== actualDepLocal) {
                                const totalMinutes = ((actualDepLocal % 1440) + 1440) % 1440;
                                const depHours = Math.floor(totalMinutes / 60);
                                const depMinutes = totalMinutes % 60;
                                const actualDepTime = `${String(depHours).padStart(2, '0')}:${String(depMinutes).padStart(2, '0')}`;
                                const depColor = depDelay > 0 ? '#c0392b' : '#27ae60';
                                return (
                                  <>
                                    <span style={{ textDecoration: 'line-through' }}>{flight.lsch_dep_time}</span>
                                    <span style={{ marginLeft: '8px', color: depColor }}>{actualDepTime}</span>
                                  </>
                                );
                              }
                            }
                            return flight.lsch_dep_time;
                          })()}
                          <strong style={{ marginLeft: '8px', marginRight: '8px' }}>→</strong>
                          {(() => {
                            const hasArrDelay = flight.lflight_arr_utc_min !== null && flight.lsch_arr_utc_min !== null;
                            if (hasArrDelay) {
                              const schedArrLocal = flight.lsch_arr_utc_min + (flight.lsch_arr_offset || 0);
                              const actualArrLocal = flight.lflight_arr_utc_min + (flight.lflight_arr_offset || 0);
                              const arrDelay = actualArrLocal - schedArrLocal;
                              if (arrDelay === 0) {
                                return (
                                  <>
                                    <span style={{ color: '#2c3e50' }}>{flight.lsch_arr_time}</span>
                                    <span style={{ fontSize: '0.9em', color: '#2ecc71', marginLeft: '8px', fontWeight: 'bold' }}>(on time)</span>
                                  </>
                                );
                              }
                              if (schedArrLocal !== actualArrLocal) {
                                const totalMinutes = ((actualArrLocal % 1440) + 1440) % 1440;
                                const arrHours = Math.floor(totalMinutes / 60);
                                const arrMinutes = totalMinutes % 60;
                                const actualArrTime = `${String(arrHours).padStart(2, '0')}:${String(arrMinutes).padStart(2, '0')}`;
                                const arrColor = arrDelay > 0 ? '#c0392b' : '#27ae60';
                                return (
                                  <>
                                    <span style={{ textDecoration: 'line-through' }}>{flight.lsch_arr_time}</span>
                                    <span style={{ marginLeft: '8px', color: arrColor }}>{actualArrTime}</span>
                                  </>
                                );
                              }
                            }
                            return flight.lsch_arr_time;
                          })()}
                        </div>
                      )}
                      {flight.fstat_cabin_type && (
                        <div>{cabinTypes[flight.fstat_cabin_type]}</div>
                      )}
                      {flight.fstat_seat_row && flight.fstat_seat_col && (
                        <div>
                          {flight.fstat_seat_row}{flight.fstat_seat_col}
                          {flight.fstat_seat_type && ` (${seatTypes[flight.fstat_seat_type]})`}
                        </div>
                      )}
                      {filter === 'future' && (flight.plane_reg || flight.plane_model) && (
                        <div>
                          {flight.plane_reg || flight.plane_msn}
                          {flight.plane_model && ` (${flight.plane_model})`}
                        </div>
                      )}
                      {flight.fstat_note && (
                        <div style={{ marginTop: '5px', fontStyle: 'italic' }}>{flight.fstat_note}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default MyFlights;
